import { useState, useEffect, useCallback } from 'react';
import { listJustifications, reviewJustification } from '../lib/justify';
import { CertificateLink } from './Certificate';
import { SearchBox, Chips, FilterBar, DateRange, TotalsBar, matchesQuery } from './Filters';

const fmt = (s) => (s ? new Date(s + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—');

// Para el administrador: aprobar o rechazar pedidos de justificación de faltas
export default function JustificacionesAdmin({ showToast, onChanged }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [st, setSt] = useState('pending');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => setList(await listJustifications().catch(() => [])), []);
  useEffect(() => { load(); }, [load]);

  const base = list.filter((r) => matchesQuery(q, r.profiles?.name, r.reason) && (!from || r.date >= from) && (!to || r.date <= to));
  const shown = base.filter((r) => st === 'all' || r.status === st);
  const cnt = (k) => base.filter((r) => k === 'all' || r.status === k).length;

  const decide = async (approve) => {
    setBusy(true);
    try {
      await reviewJustification(sel.id, approve, note);
      showToast?.(approve ? 'Falta justificada' : 'Pedido rechazado');
      setSel(null); setNote(''); await load(); onChanged?.();
    } catch (e) { showToast?.(e.message, 'error'); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <TotalsBar items={[
        {label:'Pedidos',value:base.length,tone:'sky'},
        {label:'Pendientes',value:cnt('pending'),tone:'amber'},
        {label:'Justificadas',value:cnt('approved'),tone:'green'},
        {label:'Con certificado',value:base.filter(r=>r.certificate_path).length},
      ]}/>
      <FilterBar shown={shown.length} total={list.length} noun="pedidos" active={!!(q || from || to || st !== 'pending')} onClear={() => { setQ(''); setFrom(''); setTo(''); setSt('pending'); }}>
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por empleado o motivo…" />
        <DateRange from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); }} />
        <Chips value={st} onChange={setSt} options={[{ id: 'pending', label: 'Pendientes', count: cnt('pending') }, { id: 'approved', label: 'Aprobados', count: cnt('approved') }, { id: 'rejected', label: 'Rechazados', count: cnt('rejected') }, { id: 'all', label: 'Todos', count: cnt('all') }]} />
      </FilterBar>
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">
        {shown.length === 0 && <p className="text-sm text-gray-400 text-center py-10">No hay pedidos con estos filtros</p>}
        {shown.map((r) => (
          <div key={r.id} className="px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900">{r.profiles?.name}</p>
                <p className="text-xs text-gray-500">Falta del <b>{fmt(r.date)}</b></p>
                <p className="text-xs text-gray-400 italic mt-0.5">"{r.reason}"</p>
                <div className="mt-1"><CertificateLink path={r.certificate_path} />{!r.certificate_path && <span className="text-[11px] text-gray-400">Sin certificado</span>}</div>
                {r.admin_note && <p className="text-xs text-sky-700 mt-1">Nota: {r.admin_note}</p>}
              </div>
              {r.status === 'pending'
                ? <button onClick={() => { setSel(r); setNote(''); }} className="text-xs font-bold text-white px-3 py-1.5 rounded-xl bg-sky-700 flex-shrink-0">Revisar</button>
                : <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{r.status === 'approved' ? 'Justificada' : 'Rechazada'}</span>}
            </div>
          </div>
        ))}
      </div>
      {sel && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSel(null)} />
          <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4">
            <div>
              <p className="font-bold text-gray-900">{sel.profiles?.name}</p>
              <p className="text-xs text-gray-500">Falta del {fmt(sel.date)} · "{sel.reason}"</p>
              <div className="mt-1"><CertificateLink path={sel.certificate_path} /></div>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota para el empleado (opcional)" className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm" />
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => decide(false)} className="flex-1 py-3 rounded-2xl text-sm font-bold border-2 border-red-200 text-red-600 disabled:opacity-50">Rechazar</button>
              <button disabled={busy} onClick={() => decide(true)} className="flex-1 py-3 rounded-2xl text-sm font-bold text-white bg-emerald-600 disabled:opacity-50">Justificar la falta</button>
            </div>
            <button onClick={() => setSel(null)} className="w-full text-xs text-gray-400">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
